import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import TopNavbar from "../Components/TopNavbar.jsx";
import Footer from "../Components/Footer.jsx";
import { BLOGS } from "../data/blogData.js";
import "../Assets/Css/blog-list.css";

/* ── Helpers ─────────────────────────────────────────── */

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/* ── BlogCard ─────────────────────────────────────────── */

const BlogCard = ({ post }) => (
  <Link to={`/blog/${post.slug}`} className="ik-blog-card">
    {/* Cover */}
    <div className="ik-blog-card__cover">
      {/* Background: image if available, otherwise gradient */}
      {post.coverImage ? (
        <img
          src={post.coverImage}
          alt={post.title}
          className="ik-blog-card__cover-img"
        />
      ) : (
        <div
          className="ik-blog-card__cover-bg"
          style={{ background: post.coverGradient }}
        />
      )}
      {/* Diagonal stripe texture overlay */}
      <div className="ik-blog-card__cover-pattern" />
      {/* Dark gradient overlay — always present so text is readable */}
      <div className="ik-blog-card__cover-overlay" />
      <span className="ik-blog-card__category">{post.category}</span>
    </div>

    {/* Body */}
    <div className="ik-blog-card__body">
      <div className="ik-blog-card__meta">
        <span>{formatDate(post.date)}</span>
        <span>·</span>
        <span>{post.readTime}</span>
      </div>

      <h2 className="ik-blog-card__title">{post.title}</h2>
      <p className="ik-blog-card__excerpt">{post.excerpt}</p>

      <span className="ik-blog-card__read">
        Read article
        <span className="ik-blog-card__read-arrow">→</span>
      </span>
    </div>
  </Link>
);

/* ── BlogList Page ────────────────────────────────────── */

const BlogList = () => {
  // Derive unique categories from BLOGS
  const categories = useMemo(() => {
    const cats = ["All", ...new Set(BLOGS.map((b) => b.category))];
    return cats;
  }, []);

  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = useMemo(() => {
    if (activeCategory === "All") return BLOGS;
    return BLOGS.filter((b) => b.category === activeCategory);
  }, [activeCategory]);

  // Sort newest first
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [filtered]
  );

  return (
    <div className="ik-blog-list">
      <TopNavbar />

      {/* Hero */}
      <section className="ik-blog-list__hero">
        <Container>
          <span className="ik-blog-list__hero-label">Innkeeper Insights</span>
          <h1>
            Career Guides &<br />
            <span>Industry Perspectives</span>
          </h1>
          <p>
            Real, practical guides written for ambitious Nigerians who want to
            break into tech — or grow within it. No fluff, just the honest
            path forward.
          </p>
        </Container>
      </section>

      {/* Filter bar */}
      <div className="ik-blog-list__filters">
        <Container style={{ display: "flex", gap: 10, flexWrap: "wrap", padding: 0 }}>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`ik-blog-list__filter-btn${
                activeCategory === cat ? " ik-blog-list__filter-btn--active" : ""
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </Container>
      </div>

      {/* Grid */}
      <div className="ik-blog-list__grid-wrap">
        <Container>
          <div className="ik-blog-list__grid">
            {sorted.length > 0 ? (
              sorted.map((post) => <BlogCard key={post.id} post={post} />)
            ) : (
              <p className="ik-blog-list__empty">No articles in this category yet.</p>
            )}
          </div>
        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default BlogList;